/**
 * Owner tree: the disposal and context scope for reactive computations.
 *
 * Roots, components and every effect run create an owner; computations made
 * inside register their cleanup on it, and disposing an owner runs those
 * cleanups in reverse registration order — innermost first.
 *
 * Ownership is a parent pointer only: an owner keeps no child list and
 * disposal does not cascade downward. A scope that outlives its creator (a
 * keyed repeat's per-row scope, a `cache()` entry's instance) is therefore
 * disposed by whatever owns its lifetime, not by an ancestor. Context values
 * live on owners and resolve up the parent chain (see `context.ts`), so an
 * owner that is only a generation of some longer-lived scope is reset rather
 * than replaced — see `resetOwner` (#1310).
 */
let currentOwner = null;
export function createOwner(parent = currentOwner) {
    const resolved = parent && !parent.disposed ? parent : null;
    return { parent: resolved, cleanups: null, disposed: false, context: null };
}
/** The owner's context map, created on first write. Read paths must not call this. */
export function ownerContextForWrite(owner) {
    const existing = owner.context;
    if (existing)
        return existing;
    const created = new Map();
    owner.context = created;
    return created;
}
/**
 * Tears down everything registered on `owner` while leaving the scope usable:
 * cleanups run in reverse registration order, then the context map is cleared.
 *
 * This is what a scope does between generations — an effect run before the
 * next one, a keyed repeat's row before it re-renders. Replacing the owner
 * object instead would be equivalent for the computations, but not for
 * anything still parented to it: disposal nulls `parent` and empties
 * `context`, so a surviving child would be left with a dead context ancestor
 * and `inject()` would silently fall back to defaults (#1310).
 */
export function resetOwner(owner) {
    // Detach before running, not `splice(0)`: a cleanup that registers another
    // one must land on the next generation's list rather than on the one being
    // drained, which is what the splice bought — and detaching buys it without
    // the copy the splice allocated on every teardown.
    const cleanups = owner.cleanups;
    owner.cleanups = null;
    // Every cleanup runs even when an earlier one throws (#1403): stopping at
    // the first failure abandons everything after it, and on the dispose path
    // the owner is already marked disposed, so a second call cannot recover the
    // tail. Errors surface only after the drain — the first one alone, or an
    // AggregateError when several threw — the same convention the signal flush
    // uses for effects (#1205). The zero-error path allocates nothing.
    let firstError;
    let restErrors = null;
    let failed = false;
    if (cleanups) {
        for (let index = cleanups.length - 1; index >= 0; index -= 1) {
            try {
                cleanups[index]?.();
            }
            catch (error) {
                if (failed)
                    (restErrors ??= []).push(error);
                else {
                    failed = true;
                    firstError = error;
                }
            }
        }
    }
    owner.context?.clear();
    owner.context = null;
    if (failed) {
        throw restErrors === null
            ? firstError
            : new AggregateError([firstError, ...restErrors], 'owner cleanups threw during disposal');
    }
}
/**
 * Registers `cleanup` on the current owner and returns that owner, or null
 * when nothing owns the registration. The return value is the handle
 * `removeOwnedCleanup` needs to release the entry when its owner disposes
 * early (#1395).
 */
export function registerOwnedCleanup(cleanup) {
    if (currentOwner && !currentOwner.disposed) {
        (currentOwner.cleanups ??= []).push(cleanup);
        return currentOwner;
    }
    return null;
}
/**
 * Releases a registration that `registerOwnedCleanup` made. Explicit
 * disposal — an `effect()` handle, called per keyed row on the render path —
 * must drop its entry, or a long-lived owner retains one disposer closure per
 * create/dispose cycle forever (#1395). Registration order is preserved (the
 * drain runs cleanups in reverse), so the entry is spliced out of place and
 * only the common last-registered case pops. A stale call — the entry already
 * drained with its owner — finds nothing and does nothing.
 */
export function removeOwnedCleanup(owner, cleanup) {
    const cleanups = owner.cleanups;
    if (!cleanups)
        return;
    const index = cleanups.lastIndexOf(cleanup);
    if (index === -1)
        return;
    if (index === cleanups.length - 1)
        cleanups.pop();
    else
        cleanups.splice(index, 1);
    if (cleanups.length === 0)
        owner.cleanups = null;
}
export function disposeOwner(owner) {
    if (owner.disposed)
        return;
    owner.disposed = true;
    resetOwner(owner);
    // The parent pointer stays: a child owner created before this disposal (the
    // #1310 generation path) must keep its injection chain to the ancestors
    // ABOVE this owner. Nulling it stranded those children with default values
    // even though the grandparents' context was untouched (#1522). inject()
    // skips disposed owners' maps, so the cleared context here is still
    // invisible to them.
}
export function getOwner() {
    return currentOwner;
}
export function runWithOwner(owner, fn) {
    const previous = currentOwner;
    currentOwner = owner;
    try {
        return fn();
    }
    finally {
        currentOwner = previous;
    }
}
export function createRoot(fn) {
    const owner = createOwner(currentOwner);
    try {
        const value = runWithOwner(owner, fn);
        return {
            value,
            dispose: () => disposeOwner(owner),
        };
    }
    catch (error) {
        disposeOwner(owner);
        throw error;
    }
}
export function onCleanup(fn) {
    registerOwnedCleanup(fn);
}
//# sourceMappingURL=owner.js.map